import { isApiError } from "@core/api/api-error";
import { ApiService } from "@core/api/api-service";
import {
	HealthcareTeamMember,
	PatientSummary,
	RegisteredPtInviteData,
	SharePatientOutput,
	UnregisteredPtInviteData,
} from "@domain/healthcare-team/healthcare-team";
import {
	HealthcareTeamErrorCodes,
	HealthcareTeamInvitationInWrongCountryError,
	InvitationPtAlreadyInHctError,
} from "@domain/healthcare-team/healthcare-team-errors";
import { HealthcareTeamInvitationPatientData } from "@domain/healthcare-team/healthcare-team-manager";

import { isNil } from "@utils/assert";
import { dateFromDateDto } from "@utils/date-utils";

type PatientSummaryDto = Omit<PatientSummary, "birthDate" | "creationDate"> & {
	birthDate: string;
	creationDate: string;
};

type SharePatientOutputDto = Omit<SharePatientOutput, "patient"> & {
	patient: PatientSummaryDto;
};

export class HealthcareTeamService {
	constructor(private readonly apiService: ApiService) {}

	async checkEmailRegistered(patientId: string, email: string): Promise<RegisteredPtInviteData> {
		return (await this.apiService.get<RegisteredPtInviteData>(`/patients/${patientId}/healthcare-team/${email}/check`))
			.data;
	}

	async sendInvitation(patientId: string, email: string, message?: string): Promise<void> {
		await this.apiService.post<void>(
			`/patients/${patientId}/healthcare-team/invite`,
			{
				email,
				message,
			},
			undefined,
			error => !isApiError(error) || isNil(error.code),
		);
	}

	async resendInvitation(patientId: string, hctMemberId: string): Promise<void> {
		await this.apiService.post(`/patients/${patientId}/healthcare-team/${hctMemberId}/resend-invitation`);
	}

	async sendUnregisteredInvitation(patientId: string, email: string): Promise<UnregisteredPtInviteData> {
		return (
			await this.apiService.post<UnregisteredPtInviteData>(
				`/patients/${patientId}/healthcare-team/invite-unregistered`,
				{ email },
			)
		).data;
	}

	async fetchHealthcareTeam(patientId: string): Promise<HealthcareTeamMember[]> {
		return (await this.apiService.get<HealthcareTeamMember[]>(`/patients/${patientId}/healthcare-team`)).data;
	}

	async getInvitationDetails(patientId: string): Promise<SharePatientOutput> {
		const result = (await this.apiService.get<SharePatientOutputDto>(`/pts/me/healthcare-team/${patientId}/invitation`))
			.data;
		return this.healthcareTeamOutputFromDto(result);
	}

	async confirmInvitation(patientId: string): Promise<void> {
		await this.apiService.post<void>(`/pts/me/healthcare-team/${patientId}/invitation/accept`);
	}

	async deleteMember(patientId: string, hctMemberId: string): Promise<void> {
		await this.apiService.delete<void>(`/patients/${patientId}/healthcare-team/${hctMemberId}`);
	}

	async leaveHealthcareTeam(patientId: string): Promise<void> {
		await this.apiService.delete<void>(`/patients/${patientId}/healthcare-team`);
	}

	async joinHealthcareTeam(code: string): Promise<HealthcareTeamInvitationPatientData> {
		try {
			const res = await this.apiService.post<HealthcareTeamInvitationPatientData>(
				"/pts/me/healthcare-team/join",
				{ code },
				undefined,
				() => false,
			);
			return res.data;
		} catch (e) {
			if (isApiError(e) && e.code === HealthcareTeamErrorCodes.PtAlreadyInHealthcareTeam) {
				throw new InvitationPtAlreadyInHctError();
			}
			if (isApiError(e) && e.code === HealthcareTeamErrorCodes.HealthcareTeamInvitationInWrongCountry) {
				throw new HealthcareTeamInvitationInWrongCountryError();
			}
			throw e;
		}
	}

	private healthcareTeamOutputFromDto(healthcareTeamOutputDto: SharePatientOutputDto): SharePatientOutput {
		return {
			...healthcareTeamOutputDto,
			patient: {
				...healthcareTeamOutputDto.patient,
				creationDate: dateFromDateDto(healthcareTeamOutputDto.patient.creationDate),
				birthDate: dateFromDateDto(healthcareTeamOutputDto.patient.birthDate),
			},
		};
	}
}
